/**
 * Back/forward buttons plus the breadcrumb trail, shown only inside a folder.
 *
 * The trail is the navigation HISTORY truncated at the current index, not the
 * folder's ancestor chain — going back and then into a sibling replaces the
 * forward entries, exactly like a browser. The listing owns that history; this
 * renders it and reports where the user clicked.
 *
 * Each entry is `{uuid, name}` (the root's uuid is null). Crumbs are also
 * drop targets: dragging a row onto one moves it up the tree.
 *
 * The first crumb is the home icon alone — the active tab already reads
 * "My Documents", so a labelled crumb would only repeat it.
 */

import { ChevronLeft, ChevronRight, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export function FolderPathBar({
  history,
  currentIndex,
  onNavigate,
  draggedOverFolderUuid,
  onFolderDragOver,
  onFolderDragLeave,
  onFolderDrop,
}) {
  const { t } = useTranslation(['my-docs']);

  const canGoBack = currentIndex > 0;
  const canGoForward = currentIndex < history.length - 1;

  return (
    <div className="flex items-center gap-2 mb-4">
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => canGoBack && onNavigate(currentIndex - 1)}
          disabled={!canGoBack}
          className="h-8 w-8"
          title={t('my-docs:navigation.back')}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => canGoForward && onNavigate(currentIndex + 1)}
          disabled={!canGoForward}
          className="h-8 w-8"
          title={t('my-docs:navigation.forward')}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto flex-1 min-w-0">
        {history.slice(0, currentIndex + 1).map((item, index) => (
          <div key={item.uuid || 'root'} className="flex items-center gap-2 shrink-0">
            {index > 0 && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            <button
              type="button"
              onClick={() => onNavigate(index)}
              className={cn(
                'px-2 py-1 rounded text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1.5',
                index === currentIndex
                  ? 'text-foreground bg-muted'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                draggedOverFolderUuid === item.uuid ? 'bg-blue-100 ring-2 ring-blue-500' : '',
              )}
              onDragOver={(e) => onFolderDragOver(e, item.uuid)}
              onDragLeave={onFolderDragLeave}
              onDrop={(e) => onFolderDrop(e, item.uuid)}
              title={index === 0 ? item.name : undefined}
            >
              {index === 0 ? <Home className="w-4 h-4" /> : item.name}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
